import React from 'react';
import { RotateCcw, Shuffle, AlertCircle, ArrowLeft } from 'lucide-react';
import { translations } from '../lib/i18n';
import { useData } from '../context/DataContext';

interface QuizResultsProps {
  total: number;
  correctCount: number;
  wrongCount: number;
  skippedCount: number;
  onRetryInOrder: () => void;
  onRetryShuffled: () => void;
  onRetryWrongOnly: () => void;
  onBackToLessons: () => void;
}

export const QuizResults: React.FC<QuizResultsProps> = ({
  total,
  correctCount,
  wrongCount,
  skippedCount,
  onRetryInOrder,
  onRetryShuffled,
  onRetryWrongOnly,
  onBackToLessons,
}) => {
  const { language } = useData();
  const t = translations[language];

  const percentage = total > 0 ? Math.round((correctCount / total) * 100) : 0;

  // Color determination: >=70 ok, 50-69 neutral, <50 no
  let ringColor = 'var(--no)';
  if (percentage >= 70) {
    ringColor = 'var(--ok)';
  } else if (percentage >= 50) {
    ringColor = 'var(--ink)';
  }

  let encouragement = t.encouragementNeedsWork;
  if (percentage >= 70) {
    encouragement = t.encouragementGreat;
  } else if (percentage >= 50) {
    encouragement = t.encouragementGood;
  }

  // SVG circular calculations
  const radius = 64;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="results-card" role="region" aria-label={t.scoreTitle}>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '1.5rem' }}>
        {t.scoreTitle}
      </h2>

      {/* Animated Circular Percentage Ring */}
      <div className="score-circle-wrapper" aria-label={`${percentage}%`}>
        <svg className="score-circle-svg" viewBox="0 0 160 160">
          <circle className="score-circle-bg" cx="80" cy="80" r={radius} />
          <circle
            className="score-circle-prog"
            cx="80"
            cy="80"
            r={radius}
            stroke={ringColor}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
          />
        </svg>

        <div className="score-circle-center">
          <span className="score-percent-num" style={{ color: ringColor }}>
            {percentage}%
          </span>
          <span className="score-fraction">
            {correctCount} / {total}
          </span>
        </div>
      </div>

      {/* Chips: Correct / Wrong / Skipped */}
      <div className="results-chips-row">
        <span className="score-pill correct">
          ✓ {correctCount} {t.scoreCorrect}
        </span>
        <span className="score-pill wrong">
          ✗ {wrongCount} {t.scoreWrong}
        </span>
        {skippedCount > 0 && (
          <span className="meta-chip">
            — {skippedCount} {t.scoreSkipped}
          </span>
        )}
      </div>

      {/* Encouraging message */}
      <p className="results-encouragement" dir="auto">
        {encouragement}
      </p>

      {/* Actions */}
      <div className="results-actions-grid">
        <button type="button" className="btn-primary" onClick={onRetryInOrder}>
          <RotateCcw size={18} /> {t.retryInOrder}
        </button>

        <button type="button" className="btn-ghost" onClick={onRetryShuffled}>
          <Shuffle size={18} /> {t.retryShuffled}
        </button>

        {wrongCount > 0 && (
          <button type="button" className="btn-ghost" onClick={onRetryWrongOnly}>
            <AlertCircle size={18} /> {t.retryWrongOnly} ({wrongCount})
          </button>
        )}

        <button
          type="button"
          className="btn-ghost"
          style={{ marginTop: '0.5rem' }}
          onClick={onBackToLessons}
        >
          <ArrowLeft size={18} /> {t.backToLessons}
        </button>
      </div>
    </div>
  );
};
